import { Auth } from '@zocks-communications/client-sdk-js'
import { AUTH_TOKEN_NAME, REFRESH_TOKEN_NAME } from './constants'

const ACCESS_TOKEN_EXPIRY_OFFSET = -300
const MFA_TOKEN_EXPIRY_OFFSET = 0

export interface SerializedToken {
  accessToken: string
  refreshToken: string | null
}

export class Token implements SerializedToken {
  accountId: string
  tokenUse: string
  accessTokenExpiration: number
  refreshTokenExpiration: number | null
  crossAccountGrantId: string | null

  constructor(
    readonly accessToken: string,
    readonly refreshToken: string | null = null,
  ) {
    const parsedAccessToken = Auth.parseJwt(accessToken)
    this.accountId = parsedAccessToken.account_id
    this.tokenUse = parsedAccessToken.token_use
    this.accessTokenExpiration = parsedAccessToken.exp
    this.refreshTokenExpiration = refreshToken ? Auth.parseJwt(refreshToken).exp : null
    this.crossAccountGrantId = parsedAccessToken.crossAccountGrantId
  }

  get isAccessTokenExpired(): boolean {
    return this.tokenUse === 'mfa'
      ? isExpired(this.accessTokenExpiration, MFA_TOKEN_EXPIRY_OFFSET)
      : isExpired(this.accessTokenExpiration, ACCESS_TOKEN_EXPIRY_OFFSET)
  }

  get isRefreshTokenExpired(): boolean {
    return this.refreshTokenExpiration ? isExpired(this.refreshTokenExpiration) : true
  }

  get isRenewable(): boolean {
    return this.refreshToken != null && !this.isRefreshTokenExpired
  }

  get isCrossAccountToken(): boolean {
    return this.crossAccountGrantId != null
  }

  toSerialized = (): SerializedToken => ({ accessToken: this.accessToken, refreshToken: this.refreshToken })

  static fromSerialized = (serialized: SerializedToken): Token => {
    return new Token(serialized.accessToken, serialized.refreshToken)
  }

  static fromPayload = (payload: any): Token => new Token(payload.access_token, payload.refresh_token)
}

const isExpired = (timestamp: number, offset: number = 0): boolean => timestamp + offset < getTimeInSeconds()

const getTimeInSeconds = () => new Date().getTime() / 1000

export const saveToken = (token: Token) => {
  window.localStorage.setItem(AUTH_TOKEN_NAME, token.accessToken)
  if (token.refreshToken) {
    window.localStorage.setItem(REFRESH_TOKEN_NAME, token.refreshToken)
  } else {
    window.localStorage.removeItem(REFRESH_TOKEN_NAME)
  }
}

export const readToken = (): Token | null => {
  const accessToken = window.localStorage.getItem(AUTH_TOKEN_NAME)
  if (accessToken) {
    const refreshToken = window.localStorage.getItem(REFRESH_TOKEN_NAME)
    const token = new Token(accessToken, refreshToken)
    if (!token.isAccessTokenExpired || token.isRenewable) return token
  }
  return null
}

export const removeToken = () => {
  window.localStorage.removeItem(AUTH_TOKEN_NAME)
  window.localStorage.removeItem(REFRESH_TOKEN_NAME)
}
